<script setup lang="ts">
// The service-address field. Suggestions come from our own /api/address/*
// endpoints (the provider key lives on the server), and picking one fills in
// the structured parts a dispatcher routes on — city, state, ZIP.
//
// It degrades on purpose: no provider configured, a failing provider, or a
// visitor who just types and moves on all end the same way — whatever is in
// the box is relayed as-is and the backend tags it "manual". An address
// field that stops accepting addresses would be worse than no autocomplete.
import { computed, onBeforeUnmount, ref } from 'vue'
import { NAutoComplete } from 'naive-ui'
import { getJson } from '@/shared/api/client'
import type {
    AddressSuggestion,
    AddressValue,
    ResolvedAddress,
    SuggestResponse,
} from '@/shared/api/types'

const props = withDefaults(
    defineProps<{ placeholder?: string; debounceMs?: number }>(),
    { placeholder: '123 Riverside Ave, Jacksonville FL', debounceMs: 220 },
)

const model = defineModel<AddressValue>({ required: true })

const suggestions = ref<AddressSuggestion[]>([])
const enabled = ref(true)
const resolving = ref(false)

// One session token spans an entry (keystrokes + the resolve) so a paid
// provider bills it once; a new one starts after each completed pick.
let session = crypto.randomUUID()
let timer: ReturnType<typeof setTimeout> | undefined
let inFlight: AbortController | undefined
let failures = 0

const options = computed(() =>
    suggestions.value.map((s) => ({ label: s.label, value: s.id })),
)

/** The "✓ Jacksonville, FL 32204" confirmation under a picked address. */
const confirmed = computed(() => {
    if (!model.value.place_id) return null
    const parts = [model.value.city, model.value.state].filter(Boolean).join(', ')
    return [parts, model.value.postal_code].filter(Boolean).join(' ') || null
})

function onUpdateValue(value: string) {
    // Naive reports a selection through the same channel as typing, and the
    // reported value is the option's value — a provider id no human types.
    const picked = suggestions.value.find((s) => s.id === value)
    if (picked) {
        void select(picked)
        return
    }
    // Typing invalidates any previous pick: the parts no longer describe
    // what's in the box.
    model.value = { formatted: value }
    schedule(value)
}

function schedule(query: string) {
    clearTimeout(timer)
    if (!enabled.value || query.trim().length < 3) {
        suggestions.value = []
        return
    }
    timer = setTimeout(() => void fetchSuggestions(query.trim()), props.debounceMs)
}

async function fetchSuggestions(query: string) {
    inFlight?.abort()
    const controller = new AbortController()
    inFlight = controller
    try {
        const res = await getJson<SuggestResponse>(
            `/api/address/suggest?q=${encodeURIComponent(query)}&session=${session}`,
            { signal: controller.signal },
        )
        if (!res.enabled) {
            enabled.value = false
            suggestions.value = []
            return
        }
        failures = 0
        suggestions.value = res.suggestions
    } catch (err) {
        if ((err as Error)?.name === 'AbortError') return
        suggestions.value = []
        // One blip is a blip; a second is a provider that isn't coming back
        // this session, so stop asking and leave a plain input behind.
        if (++failures >= 2) enabled.value = false
    }
}

async function select(picked: AddressSuggestion) {
    clearTimeout(timer)
    inFlight?.abort()
    suggestions.value = []
    // Show the picked line immediately; the parts land a moment later.
    model.value = { formatted: picked.label }
    resolving.value = true
    try {
        const res = await getJson<ResolvedAddress>(
            `/api/address/resolve?id=${encodeURIComponent(picked.id)}&session=${session}`,
        )
        model.value = {
            formatted: res.formatted || picked.label,
            city: res.city ?? undefined,
            state: res.state ?? undefined,
            postal_code: res.postal_code ?? undefined,
            // place_id is what marks the address as provider-verified, so it
            // is only set once the parts actually arrived.
            place_id: res.place_id ?? picked.id,
        }
    } catch {
        // Keep the visitor's chosen line; it relays as a typed address.
    } finally {
        resolving.value = false
        session = crypto.randomUUID()
    }
}

onBeforeUnmount(() => {
    clearTimeout(timer)
    inFlight?.abort()
})
</script>

<template>
    <div class="address-field">
        <NAutoComplete
            :value="model.formatted"
            :options="options"
            :placeholder="props.placeholder"
            :loading="resolving"
            :input-props="{ autocomplete: 'off' }"
            blur-after-select
            @update:value="onUpdateValue"
        />
        <p v-if="confirmed" class="resolved">✓ {{ confirmed }}</p>
    </div>
</template>

<style scoped lang="scss">
.address-field {
    display: flex;
    flex-direction: column;
    gap: var(--site-space-xs);
}

.resolved {
    margin: 0;
    font-size: 0.8rem;
    color: var(--site-accent);
}
</style>
